import {useState} from 'react'
import {DiffFileTree} from '@github-ui/diff-file-tree/file-tree'
import {CommitsDropdown} from './components/CommitsDropdown'
import {FileFilter} from './components/FileFilter'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {noop} from '@github-ui/noop'
import {
  pullRequestFilesChangedCommitPath,
  pullRequestFilesChangedCommitRangePath,
  pullRequestFilesChangedPath,
} from '@github-ui/paths'
import {useSuspensePullRequestFileTreePageData, type FileTreePayload} from './page-data/payloads/file-tree'
import {SelectedRefContextProvider} from './contexts/SelectedRefContext'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useFileFiltering, type FileFilterState} from './hooks/use-file-filtering'
import {getFileExtensions} from '@github-ui/diff-file-tree/diff-file-tree-helpers'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {clsx} from 'clsx'
import styles from './PullRequestFileTree.module.css'

type PullRequestFileTreeProps = FileTreePayload & {
  onFileSelected?: () => void
}

export function PullRequestFileTreePartial(fileTreePayload: FileTreePayload) {
  return (
    <AnalyticsProvider
      appName="pull_request"
      category="files_file_tree_partial"
      metadata={{pull_request_id: fileTreePayload.pullRequestId}}
    >
      <ErrorBoundary appName="pr_files_file_tree_partial">
        <PullRequestFileTreeWrapper fileTreePayload={fileTreePayload} />
      </ErrorBoundary>
    </AnalyticsProvider>
  )
}

function PullRequestFileTreeWrapper({fileTreePayload}: {fileTreePayload: FileTreePayload}) {
  // seed TSQ with file tree data from the payload
  useSuspensePullRequestFileTreePageData({
    pathName: fileTreePayload.pathName,
    initialData: fileTreePayload,
  })

  return <PullRequestFileTree {...fileTreePayload} />
}

/*
 * This component is intended to be visually and functionally consistent with the
 * @github-ui/commits FileTree component. Look for opportunities to upstream duplicated
 * code into the shared @github-ui/diff-file-tree package.
 */
export function PullRequestFileTree({
  baseRefOid,
  commits,
  diffs,
  lastReviewOid,
  ownerLogin,
  pullRequestNumber,
  repositoryName,
  onFileSelected = noop,
}: PullRequestFileTreeProps) {
  const onRangeUpdated = (args: {singleOrStartOid: string; endOid?: string} | undefined) => {
    let path: string
    if (!args) {
      path = pullRequestFilesChangedPath({owner: ownerLogin, repo: repositoryName, number: pullRequestNumber})
    } else if (args.endOid) {
      path = pullRequestFilesChangedCommitRangePath({
        owner: ownerLogin,
        repo: repositoryName,
        number: pullRequestNumber,
        base: args.singleOrStartOid,
        head: args.endOid,
      })
    } else {
      path = pullRequestFilesChangedCommitPath({
        owner: ownerLogin,
        repo: repositoryName,
        number: pullRequestNumber,
        commit: args.singleOrStartOid,
      })
    }

    window.location.href = path
  }

  const [fileFilterState, setFileFilterState] = useState<FileFilterState>(() => ({
    filterText: '',
    fileExtensions: getFileExtensions(diffs),
    unselectedFileExtensions: new Set<string>(),
    showCodeowners: false,
    showDeletedFiles: true,
    showGeneratedFiles: true,
    showOnlyManifestFiles: false,
    showVendorFiles: true,
    showViewedFiles: true,
  }))

  const [filteredDiffs] = useFileFiltering(diffs, fileFilterState)

  return (
    <ErrorBoundary fallback={<span>File tree failed to load.</span>}>
      <div className={clsx('d-flex flex-column gap-2', styles['react-pr-files-file-tree'])} id="pr-file-tree">
        <SelectedRefContextProvider baseRefOid={baseRefOid} path={ssrSafeWindow?.location?.pathname ?? ''}>
          <CommitsDropdown
            baseRefOid={baseRefOid}
            commits={commits}
            lastReviewOid={lastReviewOid}
            onRangeUpdated={onRangeUpdated}
          />
        </SelectedRefContextProvider>
        <FileFilter diffs={diffs} fileFilterState={fileFilterState} setFileFilterState={setFileFilterState} />
        <h2 className="sr-only">File tree</h2>
        <DiffFileTree diffs={filteredDiffs} renderPattern="traditional" onSelect={onFileSelected} />
      </div>
    </ErrorBoundary>
  )
}

try{ PullRequestFileTreePartial.displayName ||= 'PullRequestFileTreePartial' } catch {}
try{ PullRequestFileTreeWrapper.displayName ||= 'PullRequestFileTreeWrapper' } catch {}
try{ PullRequestFileTree.displayName ||= 'PullRequestFileTree' } catch {}